// ============================================================
// TMR Portal Admin — as colunas da planilha de Empresas
// ============================================================
import { STATUS_MAP } from '../../components/primitives';
import { maskCNPJ } from '../../lib/format';
import { gapSummary } from '../../data/companies/gaps';
import { BUSINESS_STATUS_OPTIONS, syncState } from '../../data/companies/status';
import type { CompanyBusinessStatus } from '../../data/companies/contract';
import type { CompanyListItem } from '../../data/companies/contract';
import type { ExportColumn } from '../../lib/export/types';

/** Status → the words the badge on screen uses. Built from the filter's own options, never retyped. */
const STATUS_LABEL = Object.fromEntries(
  BUSINESS_STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<CompanyBusinessStatus, string>;

/**
 * The Empresas list as a spreadsheet (20 §9).
 *
 * Every value goes out **as the screen shows it** — the CNPJ masked, the status in Portuguese, the two
 * managers by name — which is the whole reason the file is built in the frontend. A backend-rendered
 * export would have had to grow its own copy of these labels, and the two would disagree the first time
 * one of them was changed.
 *
 * It carries more than the table does: the table shows five columns because a screen has a width, while a
 * spreadsheet is where somebody goes precisely to get the address and the dates.
 */
export const COMPANY_EXPORT_COLUMNS: ExportColumn<CompanyListItem>[] = [
  { header: 'Empresa', value: (c) => c.name },
  { header: 'CNPJ', value: (c) => (c.cnpj ? maskCNPJ(c.cnpj) : '') },
  { header: 'Domínio', value: (c) => c.subdomain },
  { header: 'Situação', value: (c) => STATUS_LABEL[c.status] ?? c.status },
  {
    header: 'Sincronia',
    value: (c) => STATUS_MAP[syncState(c.exportStatus, c.recordOrigin)]?.label ?? c.exportStatus,
  },
  { header: 'Gestor de venda', value: (c) => c.salesManager?.name ?? '' },
  { header: 'Gestor de pós-venda', value: (c) => c.postSalesManager?.name ?? '' },
  { header: 'Logradouro', value: (c) => c.address.street },
  { header: 'Número', value: (c) => c.address.number },
  { header: 'Complemento', value: (c) => c.address.complement ?? '' },
  { header: 'Bairro', value: (c) => c.address.district },
  { header: 'Cidade', value: (c) => c.address.city },
  { header: 'UF', value: (c) => c.address.state },
  { header: 'CEP', value: (c) => c.address.zipCode },
  // The legacy producer id is what somebody cross-referencing this file against the V1 needs, and it is
  // the one column that is genuinely a number.
  { header: 'Produtor no legado', value: (c) => c.legacyProducerId },
  // What the record is missing, in the operator’s words: after the backfill this is the column somebody
  // sorts by to see how much of the base still needs completing (19 §5).
  { header: 'Cadastro pendente', value: (c) => gapSummary(c.registrationGaps) },
  { header: 'Cadastrada em', value: (c) => new Date(c.createdAt) },
];
